import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { COUNTRY_CODES } from '../constants/countries.js';
// Extend Zod with OpenAPI
extendZodWithOpenApi(z);
// Role enum (matches your database roles)
export const RoleSlugSchema = z
    .enum(['admin', 'user_manager', 'content_manager', 'instructor', 'support', 'learner'])
    .openapi({
    description: 'User role',
    example: 'learner',
});
// Creation mode
export const CreateModeSchema = z
    .enum(['password', 'invite', 'invite_link'])
    .optional()
    .default('password')
    .openapi({
    description: 'Creation mode: password (set password), invite (send invite email), invite_link (generate invite link without sending email)',
    example: 'password',
});
// Create user schema (admin creating a new user)
export const CreateUserSchema = z
    .object({
    email: z.string().email('Invalid email format').min(1, 'Email is required').openapi({
        description: 'User email address',
        example: 'john.doe@example.com',
    }),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(72, 'Password must be less than 72 characters')
        .optional()
        .openapi({
        description: 'User password (required when mode is "password")',
        example: 'SecurePass123!',
    }),
    mode: CreateModeSchema,
    roleSlug: RoleSlugSchema.optional().default('learner').openapi({
        description: 'Role to assign to the user (defaults to learner)',
    }),
    name: z.string().max(200).trim().optional().openapi({
        description: "User's display name (single canonical field)",
        example: 'John Doe',
    }),
    mobile: z.string().max(20, 'Mobile must be less than 20 characters').optional().openapi({
        description: 'User mobile phone number',
        example: '+1234567890',
    }),
    country: z.enum(COUNTRY_CODES).optional().openapi({
        description: 'ISO 3166-1 alpha-2 country code',
        example: 'AU',
    }),
})
    .refine((data) => data.mode !== 'password' || (data.password && data.password.length >= 8), {
    message: 'Password is required when mode is "password"',
    path: ['password'],
})
    .openapi('CreateUser');
// Update user schema (admin updating an existing user)
export const UpdateUserSchema = z
    .object({
    email: z.string().email('Invalid email format').optional().openapi({
        description: 'User email address',
        example: 'john.doe@example.com',
    }),
    roleSlug: RoleSlugSchema.optional().openapi({
        description: 'Role to assign to the user',
    }),
    name: z.string().max(200).trim().min(1, 'Name cannot be empty').optional().openapi({
        description: "User's display name (single canonical field)",
        example: 'John Doe',
    }),
    mobile: z.string().max(20, 'Mobile must be less than 20 characters').optional().openapi({
        description: 'User mobile phone number',
        example: '+1234567890',
    }),
    country: z.enum(COUNTRY_CODES).optional().openapi({
        description: 'ISO 3166-1 alpha-2 country code',
        example: 'AU',
    }),
    learnerOnboardingCompleted: z.boolean().optional().openapi({
        description: 'Whether the user has completed onboarding',
    }),
})
    .openapi('UpdateUser');
// User ID param validation
export const UserIdParamSchema = z
    .object({
    userId: z.string().uuid('Invalid user ID format').openapi({
        description: 'User UUID',
        example: '123e4567-e89b-12d3-a456-426614174000',
    }),
})
    .openapi('UserIdParam');
// List users query parameters
export const ListUsersQuerySchema = z
    .object({
    page: z.coerce.number().int().positive().optional().default(1).openapi({
        description: 'Page number',
        example: 1,
    }),
    pageSize: z.coerce.number().int().positive().max(100).optional().default(10).openapi({
        description: 'Number of items per page (max 100)',
        example: 10,
    }),
    sortBy: z
        .enum(['createdAt', 'updatedAt', 'email', 'name', 'role'])
        .optional()
        .default('createdAt')
        .openapi({
        description: 'Field to sort by',
        example: 'createdAt',
    }),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc').openapi({
        description: 'Sort order',
        example: 'desc',
    }),
    roleFilter: RoleSlugSchema.optional().openapi({
        description: 'Filter by role',
        example: 'instructor',
    }),
    searchQuery: z.string().max(255).optional().openapi({
        description: 'Search in email, first name, and last name',
        example: 'john',
    }),
    hardDelete: z.coerce.boolean().optional().default(false).openapi({
        description: 'If true, permanently deletes the user from both profile and Supabase Auth. If false (default), soft deletes the user (sets deleted_at timestamp in profile and soft deletes in Supabase Auth). Note: JWTs remain valid until expiry regardless of deletion type.',
        example: false,
    }),
})
    .openapi('ListUsersQuery');
// User Schema (Profile)
// `roleId` was dropped in #23 — `role` (the slug enum) is the canonical
// role field; the FK `roleId` referenced the now-deleted `roles` table.
// External consumers reading the OpenAPI spec lose the `roleId` field.
export const UserSchema = z
    .object({
    id: z.string().uuid(),
    createdAt: z.string(),
    /** Canonical full name from BetterAuth `user.name`. */
    name: z.string().nullable(),
    mobile: z.string().nullable(),
    country: z.string().nullable(),
    timezone: z.string().nullable(),
    updatedAt: z.string().nullable(),
    email: z.string().optional(),
    role: RoleSlugSchema.optional(), // Can be joined
})
    .openapi('User');
// Change password schema (admin changing a user's password)
export const ChangePasswordSchema = z
    .object({
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(72, 'Password must be less than 72 characters')
        .openapi({
        description: 'New password for the user',
        example: 'NewSecurePass123!',
    }),
})
    .openapi('ChangePassword');
// Ban user schema
export const BanUserSchema = z
    .object({
    duration: z.string().min(1, 'Duration is required').openapi({
        description: 'Ban duration (e.g., "1h", "24h", "7d", "30d", "876000h" for permanent). Use Supabase-compatible duration strings.',
        example: '24h',
    }),
})
    .openapi('BanUser');
// Role entity schema (for joined role objects from the API)
export const RoleSchema = z
    .object({
    id: z.string().uuid(),
    slug: z.string().optional(),
    name: z.string(),
    description: z.string().optional(),
})
    .openapi('Role');
// Profile schema (for /profile endpoint responses)
export const ProfileSchema = z
    .object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    email: z.string().optional(),
    /** Canonical full name from BetterAuth `user.name`. */
    name: z.string().optional(),
    avatarUrl: z.string().optional(),
    mobile: z.string().optional(),
    country: z.string().optional(),
    role: z
        .union([z.object({ slug: z.string(), name: z.string() }), z.string()])
        .optional(),
    roles: z.object({ slug: z.string(), name: z.string() }).optional(),
    preferences: z.record(z.string(), z.unknown()).optional(),
    learnerOnboardingCompleted: z.boolean().optional(),
    timezone: z.string().nullable().optional(),
    weekStartsOn: z.string().optional(),
})
    .openapi('Profile');
//# sourceMappingURL=user.schema.js.map